/* eslint-disable jsx-a11y/anchor-has-content */
import { FunctionComponent, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useCookies } from 'react-cookie';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ConfigContext } from '../../../context';
import { IS_PRODUCTION } from '../../../env';
import { userApi } from '../../../services';
import {
  Button,
  ButtonType,
  CUSTOM_CLIENT_CONFIG,
  Check,
  CookieActionType,
  IconLabel,
  InputField,
  MZ_ACCOUNT_COOKIE_KEY,
  cookieOptions,
  emailValidationError,
  getCssVar,
  getWebAppUrl,
  nameValidationError,
  useLoginRedirect,
} from '../../../shared';
import { Form } from '../Form';
import { SocialLogin } from '../Login';

import classNames from 'classnames';
import formClasses from '../Form/Form.module.scss';
import classes from './Signup.module.scss';

const TERMS_TEXT_ID = 'sign-up-terms';

export const Signup: FunctionComponent = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const location = useLocation();

  const referralCode = new URLSearchParams(location.search).get('referralCode');

  const [triggerLoginByEmail] = userApi.endpoints.userLoginByEmail.useMutation();

  const [userRegistrationByEmail] = userApi.endpoints.userRegistrationByEmail.useMutation();

  const { loginRedirect, clearLoginRedirect } = useLoginRedirect();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setCookie] = useCookies([MZ_ACCOUNT_COOKIE_KEY]);

  const { config } = useContext(ConfigContext);

  const {
    emailPlaceholder,
    supportEmail,
    termsConditions,
    login: {
      isRegular,
      social: { google, facebook, apple },
    },
    signup: { referralTitleLocaliseKey, referralIntroLocaliseKey },
  } = config.elements;

  const [email, setEmail] = useState<string>('');

  const [name, setName] = useState<string>('');

  const [password, setPassword] = useState<string>('');

  const [isEmailValid, setIsEmailValid] = useState<boolean>(true);

  const [isNameValid, setIsNameValid] = useState<boolean>(true);

  const [signedUp, setSignedUp] = useState<boolean>(false);

  const [peeping, setPeeping] = useState<boolean>(false);

  const [credentialError, setCredentialError] = useState<boolean>(false);

  const [checkedTerms, setCheckedTerms] = useState<boolean>(false);

  const [signUpError, setSignUpError] = useState<boolean>(false);

  const signUpButton = useRef<HTMLButtonElement>(null);

  const signupActive = useMemo(
    () => checkedTerms && email.length && name.length,
    [checkedTerms, email.length, name.length]
  );

  const loginActive = useMemo(() => password.length, [password.length]);

  const loginButton = useRef<HTMLButtonElement>(null);

  const passwordFieldType = useMemo(() => (peeping ? 'text' : 'password'), [peeping]);

  const emailChangeHandler = useCallback((value: string) => {
    setSignUpError(false);
    setIsEmailValid(true);
    setEmail(value);
  }, []);

  const nameChangeHandler = useCallback((value: string) => {
    setIsNameValid(true);
    setName(value);
  }, []);

  const passwordChangeHandler = useCallback((value: string) => {
    setCredentialError(false);
    setPassword(value);
  }, []);

  const signupClickHandler = useCallback(async () => {
    if (emailValidationError(email)) {
      setIsEmailValid(false);
      return;
    }

    if (nameValidationError(name)) {
      setIsNameValid(false);
      return;
    }

    signUpButton.current?.setAttribute('disabled', 'true');

    const { payload } = await userRegistrationByEmail({
      email,
      screenName: name,
      referralCode,
    }).unwrap();

    payload ? setSignedUp(true) : setSignUpError(true);

    signUpButton.current?.removeAttribute('disabled');
  }, [email, name, referralCode, userRegistrationByEmail]);

  const onSuccessLogin = useCallback(
    async ({ accessToken, webappUrl }: { accessToken: string; webappUrl: string }) => {
      if (CUSTOM_CLIENT_CONFIG?.baseHost) {
        setCookie(
          MZ_ACCOUNT_COOKIE_KEY,
          accessToken,
          cookieOptions({
            action: CookieActionType.SET,
            domain: `.${CUSTOM_CLIENT_CONFIG.baseHost}`,
          })
        );
      }

      if (loginRedirect) {
        clearLoginRedirect();
        window.location.replace(loginRedirect);
        return;
      }

      IS_PRODUCTION ? window.location.replace(webappUrl) : navigate('/');
    },
    [clearLoginRedirect, loginRedirect, navigate, setCookie]
  );

  const loginClickHandler = useCallback(async () => {
    setCredentialError(false);

    loginButton.current?.setAttribute('disabled', 'true');

    try {
      const { payload } = await triggerLoginByEmail({ email, password }).unwrap();

      const { accessToken, channel } = payload;

      onSuccessLogin({ accessToken, webappUrl: getWebAppUrl(channel.subdomain) });
    } catch (e) {
      setCredentialError(true);
    } finally {
      loginButton.current?.removeAttribute('disabled');
    }
  }, [email, onSuccessLogin, password, triggerLoginByEmail]);

  const hasSocial = useMemo(
    () => [google, facebook, apple].some((social) => social),
    [apple, facebook, google]
  );

  const renderSocial = useMemo(() => {
    if (!isRegular || !hasSocial) {
      return null;
    }

    return (
      <>
        <div className={classes['divider']}>{t('auth.or-sign-up-with')}</div>
        <SocialLogin onSuccessLogin={onSuccessLogin} />
      </>
    );
  }, [hasSocial, isRegular, onSuccessLogin, t]);

  const content = useMemo(() => {
    if (signedUp) {
      return (
        <>
          <div className={formClasses['form__content-header']}>
            <span className={formClasses['form__content-header-title']}>
              {t('auth.sign-up-enter-password-title')}
            </span>
            <div className={formClasses['form__content-header-subtitle']}>
              <Trans i18nKey={'auth.sign-up-enter-password-intro'} tOptions={{ email }}>
                We sent the password to
                <span
                  className={classNames(formClasses['form__content-link'], classes['link'])}
                  onClick={() => setSignedUp(false)}
                >
                  {email}
                </span>
                . Please check spam folder in case you haven't received the email yet
              </Trans>
            </div>
          </div>

          <InputField
            label={t('common.password')}
            value={password}
            type={passwordFieldType}
            placeholder={t('common.password-placeholder')}
            icon={
              <IconLabel
                iconId={peeping ? 'eye-crossed' : 'eye'}
                iconSize={18}
                ariaLabel={t(`common.${peeping ? 'hide' : 'show'}-password`)}
                onClick={() => setPeeping(!peeping)}
                color={getCssVar('--input-icon-color')}
                singleColor
              />
            }
            onChange={({ target }) => passwordChangeHandler(target.value)}
          />

          {credentialError && (
            <div className={formClasses['form__content-error']}>{t('auth.login-fail')}</div>
          )}

          <Button
            ref={loginButton}
            type={ButtonType.primary}
            label={t('common.login')}
            disabled={!loginActive}
            onClick={loginClickHandler}
            fullWidth
          />

          <span className={classes['text']}>
            {t('checkEmail.not-received')}&nbsp;
            <a
              href={`mailto:${supportEmail ?? 'support@tchop.io'}`}
              className={classNames(formClasses['form__content-link'], classes['link'])}
            >
              {t('common.contact-us')}
            </a>
          </span>
        </>
      );
    }

    return (
      <>
        <div className={formClasses['form__content-header']}>
          <span className={formClasses['form__content-header-title']}>
            {t(referralCode ? referralTitleLocaliseKey : 'auth.sign-up-title')}
          </span>
          <div className={formClasses['form__content-header-subtitle']}>
            {t(referralCode ? referralIntroLocaliseKey : 'auth.sign-up-intro')}
          </div>
        </div>

        <InputField
          label={t('common.name')}
          value={name}
          placeholder={t('common.name-placeholder')}
          onChange={({ target }) => nameChangeHandler(target.value)}
          error={!isNameValid}
          {...(!isNameValid && { errorMessage: t('auth.name-invalid') })}
        />

        <InputField
          label={t('common.email')}
          value={email}
          placeholder={emailPlaceholder ?? t('common.email-placeholder')}
          onChange={({ target }) => emailChangeHandler(target.value)}
          error={!isEmailValid}
          {...(!isEmailValid && { errorMessage: t('auth.email-invalid') })}
        />

        {signUpError && (
          <div className={formClasses['form__content-error']}>{t('auth.sign-up-fail')}</div>
        )}

        <div className={classes['terms']}>
          <Check checked={checkedTerms} ariaLabelledBy={TERMS_TEXT_ID} onChange={setCheckedTerms} />
          <div id={TERMS_TEXT_ID}>
            <Trans i18nKey={'auth.sign-up-t-&-c'}>
              {termsConditions.links.map((link) => (
                <a
                  key={link}
                  href={link}
                  className={classNames(formClasses['form__content-link'], classes['link'])}
                  target={'_blank'}
                  rel={'noreferrer'}
                >
                  {link}
                </a>
              ))}
            </Trans>
          </div>
        </div>

        <Button
          ref={signUpButton}
          type={ButtonType.primary}
          label={t('auth.sign-up')}
          disabled={!signupActive}
          onClick={signupClickHandler}
          fullWidth
        />

        {renderSocial}

        <Link to={'/auth/login?loginEmail=true'} className={formClasses['form__content-back']}>
          {t('auth.sign-up-has-account')}
        </Link>
      </>
    );
  }, [
    checkedTerms,
    credentialError,
    email,
    emailChangeHandler,
    emailPlaceholder,
    isEmailValid,
    isNameValid,
    loginActive,
    loginClickHandler,
    name,
    nameChangeHandler,
    password,
    passwordChangeHandler,
    passwordFieldType,
    peeping,
    referralCode,
    referralIntroLocaliseKey,
    referralTitleLocaliseKey,
    renderSocial,
    signUpError,
    signedUp,
    signupActive,
    signupClickHandler,
    supportEmail,
    t,
    termsConditions.links,
  ]);

  return <Form>{content}</Form>;
};
